<div class="new-orders" *ngIf="currentFrac$ | async">
  <h2>Replace Order</h2>

  <h4 class="mb-0">Required</h4>
  <mat-divider></mat-divider>

  <div mat-dialog-content>
    <form
      *ngIf="replacementOrderForm"
      [formGroup]="replacementOrderForm"
      class="new-orders-form"
      (ngSubmit)="replaceOrder()"
    >
      <mat-form-field>
        <mat-label>Select Mesh*</mat-label>
        <mat-select formControlName="meshId" saAutomatedSelector="lmo_orders_new__mesh_selector">
          <mat-optgroup *ngFor="let group of meshesInGroup$ | async" [label]="group.meshName">
            <mat-option *ngFor="let mesh of group.meshes" [value]="mesh.id">
              {{ mesh.type }}
              <span *ngIf="!mesh.isSupplemental; else isSupplementalMesh">
                Mesh
              </span>
            </mat-option>
          </mat-optgroup>
        </mat-select>
      </mat-form-field>

      <mat-form-field>
        <mat-label>Select Loader*</mat-label>
        <mat-select formControlName="loader" saAutomatedSelector="lmo_orders_new__loader_selector">
          <mat-select-trigger>
            <ng-container *ngIf="replacementOrderForm.get('loader').value as value; else selectLoader">
              <span [class.maxed-out]="isMaxedOut(value.fcpo?.purchaseOrder)">
                {{ value.site.name }}
              </span>
            </ng-container>
            <ng-template #selectLoader>
              Select Loader*
            </ng-template>
          </mat-select-trigger>
          <mat-option
            *ngFor="let mine of fracMines$ | async; trackBy: trackByFCPOId"
            [value]="mine"
            [class.mine-option]="!isMaxedOut(mine?.fcpo?.purchaseOrder)"
            [class.mine-option-maxed-out]="isMaxedOut(mine?.fcpo?.purchaseOrder)"
          >
            <div class="frac-mine-with-po" [class.maxed-out]="isMaxedOut(mine?.fcpo?.purchaseOrder)">
              <ng-container *ngIf="mine.downReport; else loaderUp">
                <span
                  class="mine-name warn"
                  [mdePopoverTriggerFor]="siteDownWarning"
                  mdePopoverTriggerOn="hover"
                  [mdePopoverOverlapTrigger]="false"
                  >{{ mine?.site?.name }}
                  <mat-icon class="warning">warning</mat-icon>
                </span>
                <mde-popover #siteDownWarning="mdePopover">
                  <div class="down-report-message">
                    <div class="header">
                      Loader Reported Down at {{ mine.downReport.createdTimestamp | date: 'short' }}
                    </div>
                    <div
                      class="sub-header"
                      *ngIf="mine.downReport.description && mine.downReport.description.length > 0"
                    >
                      {{ mine.downReport.reportedUser }} reported {{ mine.downReport.site?.name }} down for
                      {{ mine.downReport.eta | minutesToHuman }}: "{{ mine.downReport.description }}".
                    </div>

                    <div
                      class="sub-header"
                      *ngIf="!mine.downReport.description || mine.downReport.description.length === 0"
                    >
                      {{ mine.downReport.reportedUser }} reported {{ mine.downReport.site?.name }} down for
                      {{ mine.downReport.eta | minutesToHuman }}
                    </div>
                  </div>
                </mde-popover>
              </ng-container>
              <ng-template #loaderUp>
                <div class="mine-name">{{ mine?.site?.name }}</div>
              </ng-template>
              <div class="po-name">PO: {{ mine?.fcpo?.purchaseOrder?.name }}</div>
              <div class="wait-time" *ngIf="mine?.waitTime > 0">
                {{ mine?.waitTime | minutesToHuman }} est. In & Out Time
              </div>
              <div class="wait-time" *ngIf="mine?.waitTime === 0">-- est. In & Out Time</div>
              <div class="po-remaining">
                {{ mine?.fcpo?.purchaseOrder?.meshType }} mesh remaining:
                {{ calculatePoRemaining(mine?.fcpo?.purchaseOrder) | approxBigNumber }} lbs.
              </div>
            </div>
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field>
        <mat-label>Select Trucking Vendor*</mat-label>
        <mat-select formControlName="truckingVendor">
          <mat-option *ngFor="let fctv of truckingVendors$ | async; trackBy: trackByVendorId" [value]="fctv">
            {{ fctv.truckingVendorName }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field>
        <mat-label>Select Driver*</mat-label>
        <mat-select formControlName="driver">
          <mat-option>
            <ngx-mat-select-search
              [placeholderLabel]="'Driver'"
              [noEntriesFoundLabel]="'No matching driver found'"
              [formControl]="filterDriver"
            ></ngx-mat-select-search>
          </mat-option>
          <mat-option
            *ngFor="let driver of filteredDrivers$ | async; trackBy: filteredDrivers$ | trackByKey: 'id'"
            [value]="driver"
          >
            {{ driver.name }} - {{ driver.phone | phone }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <ng-container *saFeatureFlag="'lmoManageSubcarrierContracts'">
        <mat-form-field *ngIf="selectedVendorIsBrokerage$$ | async">
          <mat-label>Select Subcarrier</mat-label>
          <mat-select formControlName="driverSubcarrierId">
            <mat-option>
              <ngx-mat-select-search
                [placeholderLabel]="'Subcarrier'"
                [noEntriesFoundLabel]="'No matching subcarrier found'"
                [formControl]="filterSubcarrier"
              ></ngx-mat-select-search>
            </mat-option>
            <mat-option
              *ngFor="
                let subcarrier of filteredSubcarriers$$ | async;
                trackBy: filteredSubcarriers$$ | trackByKey: 'id'
              "
              [value]="subcarrier.id"
            >
              {{ subcarrier.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </ng-container>

      <mat-form-field>
        <mat-label>Select Order to Replace</mat-label>
        <mat-select formControlName="replacedOrder">
          <mat-select-trigger>
            <ng-container *ngIf="replacementOrderForm.get('replacedOrder').value as value; else noOrderSelected">
              <span>
                {{ value.orderId | orderId }}
              </span>
            </ng-container>
            <ng-template #noOrderSelected>
              No Order
            </ng-template>
          </mat-select-trigger>
          <mat-option [value]="null">
            No Order
          </mat-option>
          <mat-option
            *ngFor="
              let order of eligibleReplaceableOrders$$ | async;
              trackBy: eligibleReplaceableOrders$$ | trackByKey: 'orderId'
            "
            [value]="order"
            class="order-option"
          >
            <div class="order flex flex-col">
              <div class="flex flex-row justify-between">
                <div>{{ order.orderId | orderId }}</div>
                <div *ngIf="order.orderStatus === 'completed'; else orderInProgress">
                  Completed {{ order.completedAt | ago }}
                </div>
                <ng-template #orderInProgress>
                  <div>In-Progress</div>
                </ng-template>
              </div>
              <div>{{ order.meshType }} Mesh</div>
              <div
                class="font-bold"
                [class.text-red-500]="
                  replacementOrderForm.get('truckingVendor').value?.truckingVendorId !== order.truckingVendorId
                "
              >
                {{ order.truckingVendorName }}
              </div>
              <div>
                From <span class="font-bold">{{ order.mineName }}</span> to
                <span class="font-bold">{{ order.fracName }}</span>
              </div>
            </div>
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="replacementOrderForm.get('replacedOrder').value as value">
          {{ value.orderId | orderId }} will be canceled.
        </mat-hint>
      </mat-form-field>

      <mat-form-field>
        <input matInput placeholder="BOL Number*" value="" type="text" autocomplete="off" formControlName="bolNumber" />
      </mat-form-field>

      <mat-form-field *ngIf="!isBoxStorage">
        <input
          matInput
          placeholder="Final Load Weight*"
          value=""
          type="number"
          autocomplete="off"
          formControlName="finalLoadWeight"
        />
      </mat-form-field>

      <div *ngIf="isBoxStorage" class="w-full grid grid-cols-2 gap-1">
        <mat-form-field>
          <input
            matInput
            [placeholder]="(isTwoBoxes$$ | async) ? 'First Box ID*' : 'Box ID*'"
            value=""
            type="text"
            autocomplete="off"
            formControlName="box1Name"
          />
        </mat-form-field>

        <mat-form-field>
          <input
            matInput
            [placeholder]="(isTwoBoxes$$ | async) ? 'First Box Weight*' : 'Box Weight*'"
            value=""
            type="number"
            autocomplete="off"
            formControlName="box1Weight"
          />
        </mat-form-field>

        <ng-container *ngIf="isTwoBoxes$$ | async">
          <mat-form-field>
            <input
              matInput
              placeholder="Second Box ID*"
              value=""
              type="text"
              autocomplete="off"
              formControlName="box2Name"
            />
          </mat-form-field>

          <mat-form-field>
            <input
              matInput
              placeholder="Second Box Weight*"
              value=""
              type="number"
              autocomplete="off"
              formControlName="box2Weight"
            />
          </mat-form-field>
        </ng-container>
      </div>

      <mat-form-field *ngIf="selectedVendorIsBrokerage$$ | async">
        <mat-label>Trailer Type</mat-label>
        <mat-select formControlName="brokerTrailerTypeId">
          <mat-option [value]="null">None</mat-option>
          <mat-option
            *ngFor="let trailerType of brokerTrailerTypes$ | async; trackBy: brokerTrailerTypes$ | trackByKey: 'id'"
            [value]="trailerType.id"
          >
            {{ trailerType.trailerTypeName }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <mat-slide-toggle [formControlName]="'completeReplacementOrder'">Complete Replacement Order</mat-slide-toggle>
      <div
        *ngIf="replacementOrderForm.get('completeReplacementOrder').value; else inProgress"
        class="text-gray-700 text-sm"
      >
        Replacement order will be created in the "Complete" state.
      </div>
      <ng-template #inProgress>
        <div class="text-gray-700 text-sm">
          Replacement order will be created in the "In Progress" state.
        </div>
      </ng-template>

      <mat-slide-toggle [formControlName]="'nonBillableOrder'">Non Billable Order</mat-slide-toggle>
      <div *ngIf="replacementOrderForm.get('nonBillableOrder').value; else billable" class="text-gray-700 text-sm">
        Replacement order will be created and all charges will be set to $0.
      </div>

      <ng-template #billable>
        <div class="text-gray-700 text-sm">
          Replacement order will be created and normal charges will apply.
        </div>
      </ng-template>

      <h4 class="mb-0">Optional</h4>
      <mat-divider></mat-divider>

      <mat-form-field>
        <input
          matInput
          placeholder="Ticket Number"
          value=""
          type="text"
          autocomplete="off"
          formControlName="ticketNumber"
        />
      </mat-form-field>

      <mat-form-field>
        <input type="text" placeholder="Truck" matInput formControlName="truckId" [matAutocomplete]="auto" />
        <mat-autocomplete
          #auto="matAutocomplete"
          (closed)="clearTruck()"
          [displayWith]="displayTruckFn"
          (optionSelected)="onTruckSelected($event.option.value)"
        >
          <mat-option *ngFor="let option of truckService.truckList$ | async; trackBy: trackById" [value]="option" track>
            {{ option.name }} <span *ngIf="option.isInUse">(In Use)</span>
          </mat-option>
          <mat-option [value]="replacementOrderForm.controls['truckId'].value" *ngIf="checkForAddTruck$() | async">
            <span class="add-truck-option">+ Add New Truck</span>
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>

      <sa-network-active-button [active]="networkActive$$ | async">
        <button mat-raised-button color="primary" [disabled]="replacementOrderForm.invalid">
          Create Replacement Order
        </button>
      </sa-network-active-button>
    </form>
  </div>
</div>

<ng-template #isSupplementalMesh>
  Mesh – Supplemental
</ng-template>
